import { Injectable, ElementRef, Injector, ComponentRef } from '@angular/core';
import { Overlay, OverlayConfig, PositionStrategy, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { SeSuggestionSelectorOverlayRef } from './suggestion-selector-overlay-ref';
import { SUGGESTION_STREAM_TOKEN, FILTER_STREAM_TOKEN, LIGHT_LAYOUT_TOKEN, DEFAULT_TEXT_NAME_TOKEN, TEXTS_GROUP_TOKEN } from './suggestion-selector-overlay.token';
import { SeSuggestionSelectorOverlayComponent } from '../se-suggestion-selector-overlay.component';
import { Observable } from 'rxjs';

import { SuggestionFilter } from '../../se-smart-text-editor/suggestion-datasource/suggestion-filter';
import { CpControlSuggestions, Suggestion } from '../../se-smart-text-editor/suggestion-datasource/suggestion';

const DEFAULT_CONFIG: SeSuggestionSelectorOverlayConfig = {
    height: '100%',
    width: '100%',
    hasBackdrop: true,
    backdropClass: 'dark-backdrop',
    panelClass: 'se-suggestion-selector-overlay-panel',
    filteredSuggestionStream: null,
    lightLayout: true
};

@Injectable()
export class SeSuggestionSelectorOverlayService {
    constructor(private injector: Injector, private overlay: Overlay) { }

    open(config: SeSuggestionSelectorOverlayConfig = {}): SeSuggestionSelectorOverlayRef {
        const dialogConfig = { ...DEFAULT_CONFIG, ...config };
        const overlayRef = this.createOverlay(dialogConfig);
        const dialogRef = new SeSuggestionSelectorOverlayRef(overlayRef);
        this.attachDialogContainer(overlayRef, dialogConfig, dialogRef);
        const overlayRefSubscription =
            overlayRef.backdropClick().subscribe(() => {
                overlayRefSubscription.unsubscribe();
                dialogRef.close();
            });
        return dialogRef;
    }

    private createOverlay(config: SeSuggestionSelectorOverlayConfig) {
        const overlayConfig = this.getOverlayConfig(config);
        return this.overlay.create(overlayConfig);
    }

    private attachDialogContainer(overlayRef: OverlayRef, config: SeSuggestionSelectorOverlayConfig, dialogRef: SeSuggestionSelectorOverlayRef) {
        const injector = this.createInjector(config, dialogRef);
        const containerPortal = new ComponentPortal(SeSuggestionSelectorOverlayComponent, null, injector);
        const containerRef: ComponentRef<SeSuggestionSelectorOverlayComponent> = overlayRef.attach(containerPortal);
        return containerRef.instance;
    }

    private createInjector(config: SeSuggestionSelectorOverlayConfig, dialogRef: SeSuggestionSelectorOverlayRef): Injector {
        return Injector.create({
            parent: this.injector,
            providers: [
                { provide: SeSuggestionSelectorOverlayRef, useValue: dialogRef },
                { provide: SUGGESTION_STREAM_TOKEN, useValue: config.filteredSuggestionStream },
                { provide: FILTER_STREAM_TOKEN, useValue: config.filterStream },
                { provide: LIGHT_LAYOUT_TOKEN, useValue: config.lightLayout },
                { provide: DEFAULT_TEXT_NAME_TOKEN, useValue: config.defaultTextName },
                { provide: TEXTS_GROUP_TOKEN, useValue: config.cpGroupTexts }
            ]
        });
    }

    private getOverlayConfig(config: SeSuggestionSelectorOverlayConfig): OverlayConfig {
        let positionStrategy: PositionStrategy;
        if (!config.connectedComponentRef) {
            positionStrategy = this.overlay.position().global().right().centerVertically();
        } else {
            positionStrategy = this.overlay
                .position()
                .flexibleConnectedTo(config.connectedComponentRef)
                .withPositions([
                    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
                    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
                ]);
        }
        return new OverlayConfig({
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            panelClass: config.panelClass,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            positionStrategy
        });
    }

}

export interface SeSuggestionSelectorOverlayConfig {
    filteredSuggestionStream?: Observable<Suggestion[]>;
    filterStream?: Observable<SuggestionFilter>;
    height?: string;
    width?: string;
    panelClass?: string;
    hasBackdrop?: boolean;
    backdropClass?: string;
    connectedComponentRef?: ElementRef;
    lightLayout?: boolean;
    defaultTextName?: string;
    cpGroupTexts?: Observable<Map<string, CpControlSuggestions>>;
}
